import React, {
  useState,
  useMemo,
  Fragment,
  useCallback,
  useEffect,
} from 'react';
import { arrayOf, func, string } from 'prop-types';
import {
  chain,
  forEach,
  groupBy,
  isEmpty,
  map,
} from 'lodash';

import useFolders from '../../hooks/use-folders';

import {
  Dropdown,
  Checkbox,
  ContainerSubItem,
  ContainerItem,
  Message,
} from './styles';

const DropdownCheckbox = (props) => {
  const {
    placeholder,
    value,
    onChange,
    height,
    width,
    companyId,
  } = props;

  const [open, setOpen] = useState(false);

  const {
    groupedFolders = [],
    loading,
    error,
  } = useFolders(companyId);

  /**
   * "grouped" is an object that has "selectAll", "folders" and "folderGroup" properties.
   *  @description:
   *  grouped = {selectAll: { isAllFoldersSelected }, folders: {id, folderGroupId, checked}, folderGroup: { isFoldersGroupSelected }}
   * "selectAll" is used to know when all folders are selected.
   * "folders" is used to know when a folder is selected.
   * "folderGroup" is used to know when all folders of it's group is selected.
   */
  const grouped = useMemo(() => {
    if (isEmpty(groupedFolders)) return [];

    const group = { selectAll: { isAllFoldersSelected: false }, folders: {}, folderGroup: {} };

    const foldersId = groupBy(value, (v) => v);

    let isAllFoldersSelected = true;

    forEach(groupedFolders, (e) => {
      group.folderGroup[e.id] = { isFolderGroupSelected: false };
      let isFolderGroupSelected = true;

      forEach(e.folders, (v) => {
        group.folders[v.id] = { id: v.id, folderGroupId: e.id, checked: !!foldersId[v.id] };

        if (!foldersId[v.id]) {
          isFolderGroupSelected = false;
          isAllFoldersSelected = false;
        }
      });

      group.folderGroup[e.id].isFolderGroupSelected = isFolderGroupSelected && !isEmpty(e.folders);
      group.selectAll.isAllFoldersSelected = isAllFoldersSelected && !isEmpty(value);
    });

    return group;
  }, [groupedFolders, value]);

  const handleSelected = useCallback((checked, valueId) => {
    if (checked) {
      onChange([
        ...chain(grouped.folders).filter('checked').map('id').value(),
        valueId,
      ]);
    } else {
      onChange(chain(grouped.folders).filter((v) => v.id !== valueId && v.checked).map('id').value());
    }

    setOpen(true);
  }, [grouped.folders, onChange]);

  const handleSelectFolderGroup = (groupFolder, checked, folderGroupId) => {
    const group = chain(grouped.folders).filter((folder) => folder.folderGroupId !== folderGroupId && folder.checked).map('id').value();
    if (checked) {
      onChange([
        ...group,
        ...map(groupFolder, 'id')]);
    } else {
      onChange(group);
    }

    setOpen(true);
  };

  const handleSelectAll = useCallback((checked) => {
    onChange(checked ? map(grouped.folders, 'id') : []);
    setOpen(true);
  }, [grouped.folders, onChange]);

  const handleClose = useCallback((event) => {
    const { target } = event || {};
    const clickedInsideItem = target?.closest?.('.item');

    if (clickedInsideItem) {
      setOpen(true);
      return;
    }

    setOpen(false);
  }, [setOpen]);

  useEffect(() => {
    if (!loading) {
      onChange(map(grouped.folders, 'id'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, loading]);

  const text = placeholder || `${value.length} Folder${value.length > 1 ? 's' : ''} Selected`;

  return (
    <Dropdown
      open={open}
      onClose={handleClose}
      onOpen={() => setOpen(true)}
      text={text}
      closeOnChange={false}
      fluid
      className="selection"
      height={height}
      width={width}
      loading={loading}
      error={error}
    >

      <Dropdown.Menu>
        {error && (
          <Message
            error
            header="Error"
            content="Something went wrong"
          />
        )}

        {!error && !loading
          && (
            <>
              <Dropdown.Item>
                <Checkbox
                  checked={grouped?.selectAll?.isAllFoldersSelected}
                  label="Select all"
                  onFocus={() => setOpen(true)}
                  onChange={(e, { checked }) => handleSelectAll(checked)}
                />
              </Dropdown.Item>

              {!isEmpty(groupedFolders) && map(groupedFolders, (row) => {
                const { folders } = row;

                return (
                  <Fragment key={row.id}>
                    <Dropdown.Item>
                      <ContainerItem>
                        <Checkbox
                          checked={grouped.folderGroup[row.id].isFolderGroupSelected}
                          label={row.name}
                          disabled={isEmpty(folders)}
                          onFocus={() => setOpen(true)}
                          onChange={(e, { checked }) => { handleSelectFolderGroup(folders, checked, row.id); }}
                        />
                      </ContainerItem>
                    </Dropdown.Item>

                    {map(row.folders, (folder) => {
                      const checkFolder = grouped.folders[folder.id] || { checked: false };

                      return (
                        <Dropdown.Item key={folder.id}>
                          <ContainerSubItem>
                            <Checkbox
                              checked={checkFolder.checked}
                              label={folder.name}
                              onFocus={() => setOpen(true)}
                              onChange={(e, { checked }) => { handleSelected(checked, folder.id); }}
                            />
                          </ContainerSubItem>
                        </Dropdown.Item>
                      );
                    })}

                  </Fragment>
                );
              })}
            </>
          )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

DropdownCheckbox.propTypes = {
  placeholder: string,
  value: arrayOf(string),
  onChange: func.isRequired,
  height: string,
  width: string,
  companyId: string.isRequired,
};

DropdownCheckbox.defaultProps = {
  value: [],
  height: undefined,
  width: undefined,
  placeholder: null,
};

export default DropdownCheckbox;
